import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Search,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
  User as UserIcon,
  Warehouse,
  MapPin,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Shared dialog for Preia / Preda Echipament flows.
 *
 * Props:
 *   - action: "pickup" | "handover"
 *   - presetSourcePerson: optional Person object → pre-fill source for pickup (used from dismissal flow)
 *   - lockSource: when true, source person cannot be changed (used from dismissal flow)
 *   - onClose, onDone
 *
 * Behavior (pickup):
 *   1. User selects source (person OR "magazie / locație") — REQUIRED.
 *   2. Items list is filtered to ONLY items assigned to that source person
 *      (assignee_person_id === source.id). For "magazie" source, all items shown.
 *   3. User selects destination (default / locație / persoană) — like PickupTargetDialog.
 *   4. Notes + operation date (mandatory).
 */
export function EquipmentTransferDialog({ action, onClose, onDone, presetSourcePerson = null, lockSource = false }) {
  const { user } = useAuth();
  const isPickup = action === "pickup";
  const isAdmin = user?.role === "admin";

  // Source: either a person or "magazie / locație" free-text
  const [sourceMode, setSourceMode] = useState(presetSourcePerson ? "person" : "person"); // "person" | "stock"
  const [sourcePersonQ, setSourcePersonQ] = useState("");
  const [sourcePerson, setSourcePerson] = useState(presetSourcePerson);
  const [sourceFreeName, setSourceFreeName] = useState("");

  // Items + persons + users (admin needs users for picker picker)
  const [items, setItems] = useState([]);
  const [persons, setPersons] = useState([]);
  const [pickerUsers, setPickerUsers] = useState([]);  // users with pickup_location set
  const [meta, setMeta] = useState({ locations: [] });
  const [itemQ, setItemQ] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());  // bulk pickup

  // Picker (admin override): cine a preluat efectiv. Default = current user.
  const [pickerUserId, setPickerUserId] = useState("");  // "" = current user (admin)

  // Destination (pickup only): "default" | "location" | "person"
  const [destMode, setDestMode] = useState("default");
  const [destLocation, setDestLocation] = useState("");
  const [destPersonQ, setDestPersonQ] = useState("");
  const [destPersonId, setDestPersonId] = useState("");

  // Handover: target person picker
  const [handoverPersonQ, setHandoverPersonQ] = useState("");
  const [handoverPerson, setHandoverPerson] = useState(null);

  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const todayIso = new Date().toISOString().slice(0, 10);
  const [operationDate, setOperationDate] = useState(todayIso);

  useEffect(() => {
    api.get("/inventory").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    api.get("/persons").then((r) => setPersons(r.data || [])).catch(() => setPersons([]));
    if (isPickup) {
      api.get("/inventory/meta").then((r) => setMeta(r.data || { locations: [] })).catch(() => {});
      if (isAdmin) {
        // Lista utilizatorilor cu pickup_location → posibili „pickers"
        api.get("/users").then((r) => {
          const list = (r.data?.users || r.data || []).filter((u) => u.pickup_location);
          setPickerUsers(list);
        }).catch(() => setPickerUsers([]));
      }
    }
  }, [isPickup, isAdmin]);

  // When source person changes, reset selection (item filter changes)
  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [sourcePerson?.id, sourceMode]);

  // When picker changes, suggest destLocation from picker.pickup_location (only if destMode == "default")
  const pickerObj = useMemo(() => pickerUsers.find((u) => u.id === pickerUserId) || null, [pickerUsers, pickerUserId]);

  const filteredSourcePersons = useMemo(() => {
    const list = (persons || []).filter((p) => p.active !== false);
    if (!sourcePersonQ.trim()) return list.slice(0, 30);
    const q = sourcePersonQ.toLowerCase();
    return list.filter((p) =>
      ((p.nume_full || `${p.prenume || ""} ${p.nume || ""}`).toLowerCase().includes(q))
    ).slice(0, 50);
  }, [persons, sourcePersonQ]);

  // For handover, target person picker
  const filteredHandoverPersons = useMemo(() => {
    const list = (persons || []).filter((p) => p.active !== false);
    if (!handoverPersonQ.trim()) return list.slice(0, 30);
    const q = handoverPersonQ.toLowerCase();
    return list.filter((p) =>
      ((p.nume_full || `${p.prenume || ""} ${p.nume || ""}`).toLowerCase().includes(q))
    ).slice(0, 50);
  }, [persons, handoverPersonQ]);

  // For pickup destination person picker (exclude source person)
  const filteredDestPersons = useMemo(() => {
    const list = (persons || []).filter((p) => p.active !== false && p.id !== sourcePerson?.id);
    if (!destPersonQ.trim()) return list.slice(0, 30);
    const q = destPersonQ.toLowerCase();
    return list.filter((p) =>
      ((p.nume_full || `${p.prenume || ""} ${p.nume || ""}`).toLowerCase().includes(q))
    ).slice(0, 50);
  }, [persons, destPersonQ, sourcePerson]);

  // Items filtered by source (pickup) — strict filtering by assignee_person_id when a person is chosen
  const filteredItems = useMemo(() => {
    let pool = items || [];
    if (isPickup && sourceMode === "person" && sourcePerson) {
      pool = pool.filter((i) => i.assignee_person_id === sourcePerson.id);
    } else if (isPickup && sourceMode === "stock") {
      // No filter — show all items (recording a return from outside source)
    }
    if (!itemQ.trim()) return pool.slice(0, 100);
    const q = itemQ.toLowerCase();
    return pool.filter((i) =>
      (i.name || "").toLowerCase().includes(q) ||
      (i.note || "").toLowerCase().includes(q) ||
      (i.location || "").toLowerCase().includes(q) ||
      (i.serial || "").toLowerCase().includes(q)
    ).slice(0, 100);
  }, [items, itemQ, isPickup, sourceMode, sourcePerson]);

  const sourcePicked = isPickup
    ? (sourceMode === "person" ? !!sourcePerson : true)
    : true;

  const submit = async () => {
    if (isPickup) {
      if (selectedItemIds.size === 0) { toast.warning("Selectează cel puțin un echipament"); return; }
    } else {
      if (selectedItemIds.size !== 1) { toast.warning("Pentru predare selectează un singur echipament"); return; }
    }
    if (!operationDate) { toast.warning(isPickup ? "Specifică data preluării" : "Specifică data predării"); return; }
    if (isPickup) {
      if (sourceMode === "person" && !sourcePerson) { toast.warning("Selectează persoana de la care preiei"); return; }
      if (destMode === "location" && !destLocation) { toast.warning("Alege locația destinație"); return; }
      if (destMode === "person" && !destPersonId) { toast.warning("Alege persoana destinație"); return; }
    } else {
      if (!handoverPerson) { toast.warning("Selectează angajatul care primește"); return; }
    }

    setSubmitting(true);
    try {
      const itemIdsArr = Array.from(selectedItemIds);
      const payload = {
        action,
        item_ids: itemIdsArr,
        note: note || null,
        operation_date: operationDate || null,
      };
      if (isPickup) {
        if (sourceMode === "person" && sourcePerson) {
          payload.counterparty_person_id = sourcePerson.id;
        } else if (sourceMode === "stock") {
          payload.counterparty_name = sourceFreeName || null;
        }
        if (destMode === "location") payload.target_location = destLocation;
        if (destMode === "person") payload.target_person_id = destPersonId;
        if (isAdmin && pickerUserId) payload.picker_user_id = pickerUserId;
      } else {
        payload.counterparty_person_id = handoverPerson.id;
      }
      const resp = await api.post("/inventory/transfers", payload);
      const processed = resp.data?.processed ?? itemIdsArr.length;

      let dest = "";
      if (isPickup) {
        if (destMode === "location") dest = ` → ${destLocation}`;
        else if (destMode === "person") {
          const p = persons.find((x) => x.id === destPersonId);
          dest = p ? ` → ${p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}` : "";
        } else {
          const loc = resp.data?.results?.[0]?.location || pickerObj?.pickup_location || user?.pickup_location || "magazie";
          dest = ` → ${loc}`;
        }
        toast.success(`${processed} ${processed === 1 ? "echipament preluat" : "echipamente preluate"}${dest}`);
      } else {
        toast.success(`Echipament predat către ${handoverPerson.nume_full || `${handoverPerson.prenume || ""} ${handoverPerson.nume || ""}`.trim()}`);
      }
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la înregistrare");
    } finally {
      setSubmitting(false);
    }
  };

  const color = isPickup ? "#16A34A" : "#EA580C";
  const Icon = isPickup ? ArrowDownToLine : ArrowUpFromLine;
  const title = isPickup ? "Preia echipament" : "Predă echipament";
  const subtitle = isPickup
    ? "Selectează întâi de la cine preiei — vor apărea doar echipamentele alocate acelei persoane."
    : "Predă un echipament unui angajat. Va primi notificare în aplicație.";

  const defaultPickupLabel = user?.pickup_location || "SERGHEI ACASA (PSA) / magazie";

  return (
    <Dialog open onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="equipment-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color }}>
            <Icon size={18}/> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-600 -mt-2">{subtitle}</div>

        {/* ===== PICKUP FLOW ===== */}
        {isPickup ? (
          <div className="space-y-4 mt-2">
            {/* STEP 1 — Source */}
            <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-2 flex items-center gap-1.5">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px]">1</span>
                De la cine preiei? <span className="text-red-600">*</span>
              </div>

              {!lockSource && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setSourceMode("person")}
                    className={`p-2.5 rounded-md border-2 text-left transition-all ${sourceMode === "person" ? "border-emerald-500 bg-white shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                    data-testid="pickup-source-mode-person"
                  >
                    <UserIcon size={16} className={sourceMode === "person" ? "text-emerald-700" : "text-zinc-500"}/>
                    <div className="font-bold text-xs mt-1">De la angajat</div>
                    <div className="text-[10px] text-zinc-500">Vor apărea doar echipamentele lui</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSourceMode("stock")}
                    className={`p-2.5 rounded-md border-2 text-left transition-all ${sourceMode === "stock" ? "border-emerald-500 bg-white shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                    data-testid="pickup-source-mode-stock"
                  >
                    <Warehouse size={16} className={sourceMode === "stock" ? "text-emerald-700" : "text-zinc-500"}/>
                    <div className="font-bold text-xs mt-1">Din magazie / altă sursă</div>
                    <div className="text-[10px] text-zinc-500">Fără filtru pe angajat</div>
                  </button>
                </div>
              )}

              {sourceMode === "person" ? (
                lockSource && sourcePerson ? (
                  <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2 py-1.5 flex items-center gap-1.5">
                    <UserCheck size={12}/> {sourcePerson.nume_full || `${sourcePerson.prenume || ""} ${sourcePerson.nume || ""}`.trim()}
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                      <Input
                        value={sourcePersonQ}
                        onChange={(e) => setSourcePersonQ(e.target.value)}
                        placeholder="Caută angajat…"
                        className="pl-7 text-sm h-9"
                        data-testid="pickup-source-person-search"
                      />
                    </div>
                    <div className="mt-1 border border-zinc-200 rounded-md max-h-40 overflow-y-auto bg-white">
                      {filteredSourcePersons.length === 0 ? (
                        <div className="p-3 text-xs text-zinc-400">Niciun angajat găsit.</div>
                      ) : filteredSourcePersons.map((p) => {
                        const isSel = sourcePerson?.id === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSourcePerson(p)}
                            className={`w-full text-left px-2.5 py-1.5 text-xs border-b last:border-b-0 transition-colors ${isSel ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-zinc-50"}`}
                            data-testid={`pickup-source-person-${p.id}`}
                          >
                            {isSel && "✓ "}
                            {p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}
                            {p.functie && <span className="text-[10px] text-zinc-500 ml-1.5">· {p.functie}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {sourcePerson && (
                      <div className="mt-2 text-[11px] bg-emerald-100 border border-emerald-300 text-emerald-900 rounded px-2 py-1.5 flex items-center justify-between">
                        <span className="font-semibold">✓ {sourcePerson.nume_full || `${sourcePerson.prenume || ""} ${sourcePerson.nume || ""}`.trim()}</span>
                        <button type="button" onClick={() => setSourcePerson(null)} className="text-emerald-700 hover:text-emerald-900 text-[11px] underline">Schimbă</button>
                      </div>
                    )}
                  </>
                )
              ) : (
                <Input
                  className="text-xs h-9"
                  placeholder="Sursa (ex: 'Magazie', 'Șantier X') — opțional"
                  value={sourceFreeName}
                  onChange={(e) => setSourceFreeName(e.target.value)}
                  data-testid="pickup-source-freename"
                />
              )}
            </div>

            {/* STEP 2 — Item picker (filtered, MULTI-SELECT) */}
            <div className="border border-zinc-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-2 flex items-center gap-1.5">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px]">2</span>
                Echipamente <span className="text-red-600">*</span>
                <span className="ml-auto text-[10px] normal-case font-normal text-emerald-700">
                  {isPickup && sourceMode === "person" && sourcePerson
                    ? `Doar echipamentele lui ${sourcePerson.nume_full || `${sourcePerson.prenume || ""} ${sourcePerson.nume || ""}`.trim()} (${filteredItems.length})`
                    : `${filteredItems.length} disponibile`}
                  {selectedItemIds.size > 0 && <span className="ml-1 font-bold">· {selectedItemIds.size} bifate</span>}
                </span>
              </div>

              {!sourcePicked ? (
                <div className="text-xs text-zinc-500 italic p-3 bg-amber-50 border border-amber-200 rounded">
                  ⚠️ Alege întâi sursa (de la cine preiei) ca să poți vedea echipamentele disponibile.
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                    <Input
                      value={itemQ}
                      onChange={(e) => setItemQ(e.target.value)}
                      placeholder="Caută după nume, serie, locație…"
                      className="pl-7 text-sm h-9"
                      data-testid="transfer-item-search"
                    />
                  </div>
                  {/* Select all / clear bar */}
                  {filteredItems.length > 0 && (
                    <div className="flex items-center gap-2 mt-1 px-1">
                      <button
                        type="button"
                        onClick={() => setSelectedItemIds(new Set(filteredItems.map((i) => i.id)))}
                        className="text-[10px] text-emerald-700 hover:underline font-semibold"
                        data-testid="transfer-select-all"
                      >
                        ✓ Selectează toate ({filteredItems.length})
                      </button>
                      <span className="text-zinc-300">·</span>
                      <button
                        type="button"
                        onClick={() => setSelectedItemIds(new Set())}
                        disabled={selectedItemIds.size === 0}
                        className="text-[10px] text-zinc-600 hover:text-zinc-900 disabled:opacity-30"
                        data-testid="transfer-clear-all"
                      >
                        Deselectează tot
                      </button>
                    </div>
                  )}
                  <div className="mt-1 border border-zinc-200 rounded-md max-h-56 overflow-y-auto">
                    {filteredItems.length === 0 ? (
                      <div className="p-3 text-xs text-zinc-400">
                        {sourceMode === "person" && sourcePerson
                          ? `Nu există echipamente alocate persoanei „${sourcePerson.nume_full || `${sourcePerson.prenume || ""} ${sourcePerson.nume || ""}`.trim()}".`
                          : "Niciun echipament găsit."}
                      </div>
                    ) : filteredItems.map((it) => {
                      const isSel = selectedItemIds.has(it.id);
                      const toggle = () => {
                        setSelectedItemIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(it.id)) next.delete(it.id); else next.add(it.id);
                          return next;
                        });
                      };
                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={toggle}
                          className={`w-full text-left px-2.5 py-2 text-xs border-b last:border-b-0 transition-colors flex items-start gap-2 ${isSel ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-zinc-50"}`}
                          data-testid={`transfer-item-${it.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={(e) => { e.stopPropagation(); toggle(); }}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 accent-emerald-600 shrink-0"
                            data-testid={`transfer-item-check-${it.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Package size={11} className="shrink-0"/>
                              <span className="truncate">
                                {it.item_type === "psa" && <span className="text-[9px] bg-cyan-100 text-cyan-800 px-1 rounded mr-1">PSA</span>}
                                {it.name}
                              </span>
                              {it.color && <span className="w-2 h-2 rounded-full ml-auto shrink-0" style={{ background: it.color }}/>}
                            </div>
                            <div className="text-[10px] text-zinc-500 truncate mt-0.5 font-mono">
                              {it.serial ? `SN: ${it.serial} · ` : ""}{it.note || "—"}{it.location && ` · 📍 ${it.location}`}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedItemIds.size > 0 && (
                    <div className="mt-2 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2 py-1.5" data-testid="transfer-selected-summary">
                      ✓ {selectedItemIds.size} {selectedItemIds.size === 1 ? "echipament selectat" : "echipamente selectate"}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* STEP 2.5 — Picker (admin only) — cine a preluat efectiv */}
            {isAdmin && selectedItemIds.size > 0 && (
              <div className="border border-zinc-200 rounded-lg p-3 bg-blue-50/40">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-2 flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center text-[10px]">👤</span>
                  Cine a preluat efectiv? <span className="text-zinc-400 normal-case font-normal">(opțional — implicit tu)</span>
                </div>
                <Select value={pickerUserId || "__me"} onValueChange={(v) => setPickerUserId(v === "__me" ? "" : v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="pickup-picker-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__me">📌 Eu ({user?.name || user?.username || "Administrator"})</SelectItem>
                    {pickerUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`pickup-picker-${u.id}`}>
                        🏠 {u.name || u.username} → <span className="text-emerald-700 font-mono">{u.pickup_location}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pickerObj && (
                  <div className="mt-2 text-[11px] text-blue-800 bg-blue-100 border border-blue-300 rounded px-2 py-1.5">
                    💡 Destinația „Magazie/Default" va folosi <strong>{pickerObj.pickup_location}</strong> (pickup_location-ul lui {pickerObj.name || pickerObj.username}).
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — Destination */}
            {selectedItemIds.size > 0 && (
              <div className="border border-zinc-200 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-2 flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px]">3</span>
                  Unde merg echipamentele? <span className="text-red-600">*</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setDestMode("default")}
                    className={`p-2.5 rounded-md border-2 text-left transition-all ${destMode === "default" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                    data-testid="pickup-dest-default"
                  >
                    <Warehouse size={16} className={destMode === "default" ? "text-emerald-700" : "text-zinc-500"}/>
                    <div className="font-bold text-xs mt-1">Magazie/Default</div>
                    <div className="text-[10px] text-zinc-500 line-clamp-2">{pickerObj?.pickup_location || defaultPickupLabel}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDestMode("location")}
                    className={`p-2.5 rounded-md border-2 text-left transition-all ${destMode === "location" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                    data-testid="pickup-dest-location"
                  >
                    <MapPin size={16} className={destMode === "location" ? "text-emerald-700" : "text-zinc-500"}/>
                    <div className="font-bold text-xs mt-1">Locație custom</div>
                    <div className="text-[10px] text-zinc-500">Garaj, șantier etc.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDestMode("person")}
                    className={`p-2.5 rounded-md border-2 text-left transition-all ${destMode === "person" ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                    data-testid="pickup-dest-person"
                  >
                    <UserCheck size={16} className={destMode === "person" ? "text-emerald-700" : "text-zinc-500"}/>
                    <div className="font-bold text-xs mt-1">Direct la angajat</div>
                    <div className="text-[10px] text-zinc-500">Transfer P2P</div>
                  </button>
                </div>

                {destMode === "location" && (
                  <Select value={destLocation} onValueChange={setDestLocation}>
                    <SelectTrigger data-testid="pickup-dest-location-select"><SelectValue placeholder="— Alege locația —"/></SelectTrigger>
                    <SelectContent>
                      {(meta?.locations || []).length === 0 && (
                        <div className="px-2 py-1 text-xs text-zinc-400 italic">Nicio locație definită</div>
                      )}
                      {(meta?.locations || []).map((loc) => (
                        <SelectItem key={loc} value={loc}>📍 {loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {destMode === "person" && (
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                      <Input
                        value={destPersonQ}
                        onChange={(e) => setDestPersonQ(e.target.value)}
                        placeholder="Caută persoana destinație…"
                        className="pl-7 text-sm h-9"
                        data-testid="pickup-dest-person-search"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded">
                      {filteredDestPersons.length === 0 ? (
                        <div className="p-3 text-xs text-zinc-400 italic">Nicio persoană găsită.</div>
                      ) : filteredDestPersons.map((p) => {
                        const isSel = destPersonId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setDestPersonId(p.id)}
                            className={`w-full text-left px-2.5 py-1.5 text-xs border-b last:border-b-0 transition-colors ${isSel ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-zinc-50"}`}
                            data-testid={`pickup-dest-person-${p.id}`}
                          >
                            {isSel && "✓ "}
                            {p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* ===== HANDOVER FLOW ===== */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <Label className="small-label">1. Echipament</Label>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                <Input
                  value={itemQ}
                  onChange={(e) => setItemQ(e.target.value)}
                  placeholder="Caută după nume, serie, locație…"
                  className="pl-7 text-sm"
                  data-testid="transfer-item-search"
                />
              </div>
              <div className="mt-1 border border-zinc-200 rounded-md max-h-56 overflow-y-auto">
                {filteredItems.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-400">Niciun echipament găsit.</div>
                ) : filteredItems.map((it) => {
                  const isSel = selectedItemIds.has(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setSelectedItemIds(new Set([it.id]))}
                      className={`w-full text-left px-2.5 py-2 text-xs border-b last:border-b-0 transition-colors ${isSel ? "bg-orange-50 text-orange-900 font-semibold" : "hover:bg-zinc-50"}`}
                      data-testid={`transfer-item-${it.id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Package size={11} className="shrink-0"/>
                        <span className="truncate">{it.name}</span>
                        {it.color && <span className="w-2 h-2 rounded-full ml-auto shrink-0" style={{ background: it.color }}/>}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate mt-0.5 font-mono">
                        {it.note || "—"} {it.location && <span>· 📍 {it.location}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedItemIds.size === 1 && (() => {
                const it = items.find((i) => selectedItemIds.has(i.id));
                return it ? (
                  <div className="mt-2 text-[11px] bg-orange-50 border border-orange-200 text-orange-800 rounded px-2 py-1.5">
                    ✓ {it.name}{it.note ? ` · ${it.note}` : ""}
                  </div>
                ) : null;
              })()}
            </div>
            <div>
              <Label className="small-label">2. Către angajat <span className="text-red-600">*</span></Label>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                <Input
                  value={handoverPersonQ}
                  onChange={(e) => setHandoverPersonQ(e.target.value)}
                  placeholder="Caută angajat…"
                  className="pl-7 text-sm"
                  data-testid="transfer-person-search"
                />
              </div>
              <div className="mt-1 border border-zinc-200 rounded-md max-h-56 overflow-y-auto">
                {filteredHandoverPersons.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-400">Niciun angajat găsit.</div>
                ) : filteredHandoverPersons.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setHandoverPerson(p)}
                    className={`w-full text-left px-2.5 py-2 text-xs border-b last:border-b-0 transition-colors ${handoverPerson?.id === p.id ? "bg-orange-50 text-orange-900 font-semibold" : "hover:bg-zinc-50"}`}
                    data-testid={`transfer-person-${p.id}`}
                  >
                    {p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}
                    {p.functie && <span className="text-[10px] text-zinc-500 ml-1.5">· {p.functie}</span>}
                  </button>
                ))}
              </div>
              {handoverPerson && (
                <div className="mt-2 text-[11px] bg-orange-50 border border-orange-200 text-orange-800 rounded px-2 py-1.5 flex items-center justify-between">
                  <span>✓ {handoverPerson.nume_full || `${handoverPerson.prenume || ""} ${handoverPerson.nume || ""}`.trim()}</span>
                  <button type="button" onClick={() => setHandoverPerson(null)} className="text-orange-700 hover:text-orange-900 text-xs">Schimbă</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes + Operation Date — shared by both flows */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div className="md:col-span-2">
            <Label className="small-label">Observații (opțional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isPickup ? "Ex: Echipament primit în stare bună" : "Ex: Predat pentru lucrarea X"}
              data-testid="transfer-note"
            />
          </div>
          <div>
            <Label className="small-label flex items-center gap-1">📅 Data {isPickup ? "preluării" : "predării"} <span className="text-red-600">*</span></Label>
            <Input
              type="date"
              value={operationDate}
              onChange={(e) => setOperationDate(e.target.value)}
              max={todayIso}
              required
              data-testid="transfer-operation-date"
              className="text-sm"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Default azi (obligatoriu).</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button
            onClick={submit}
            disabled={submitting || selectedItemIds.size === 0 || !operationDate}
            style={{ background: color }}
            className="text-white hover:opacity-90"
            data-testid="transfer-submit-btn"
          >
            <Icon size={14} className="mr-1.5"/>
            {submitting
              ? "Se înregistrează…"
              : isPickup
                ? `Preia${selectedItemIds.size > 1 ? ` ${selectedItemIds.size}` : ""}`
                : "Predă"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** The 2 cards (Preia/Preda) to render on Dashboard/Employee Dashboard. */
export function EquipmentTransferCards() {
  const [open, setOpen] = useState(null); // "pickup" | "handover" | null

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setOpen("pickup")}
          className="card-flat p-5 text-left hover:shadow-md transition-all border-l-4 border-l-emerald-500 group"
          data-testid="preia-equip-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-emerald-50 grid place-items-center text-emerald-600 group-hover:scale-105 transition-transform">
              <ArrowDownToLine size={22}/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">Preia</div>
              <div className="font-display text-lg font-bold mt-0.5">Preia Echipament</div>
              <div className="text-xs text-zinc-500 mt-1">Alege întâi angajatul → vezi doar echipamentele lui → alege destinația.</div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setOpen("handover")}
          className="card-flat p-5 text-left hover:shadow-md transition-all border-l-4 border-l-orange-500 group"
          data-testid="preda-equip-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-orange-50 grid place-items-center text-orange-600 group-hover:scale-105 transition-transform">
              <ArrowUpFromLine size={22}/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">Predă</div>
              <div className="font-display text-lg font-bold mt-0.5">Predă Echipament</div>
              <div className="text-xs text-zinc-500 mt-1">Predă un echipament către un angajat — primește notificare în aplicație.</div>
            </div>
          </div>
        </button>
      </div>

      {open && (
        <EquipmentTransferDialog
          action={open}
          onClose={() => setOpen(null)}
          onDone={() => {}}
        />
      )}
    </>
  );
}
